<template>
  <div>
    <v-text-field
      :model-value="modelValue"
      :label="label"
      :placeholder="placeholder"
      :density="density"
      variant="outlined"
      clearable
      @update:model-value="onInput"
    >
      <template #prepend-inner>
        <v-icon v-if="isValidIcon" size="20" :color="previewColor">{{
          modelValue
        }}</v-icon>
        <v-icon v-else-if="modelValue" size="20" color="error"
          >mdi-help-circle</v-icon
        >
        <v-icon v-else size="20" color="grey">mdi-tag</v-icon>
      </template>
    </v-text-field>

    <div class="text-caption mb-2">{{ gridLabel }}</div>
    <div
      class="icon-grid border rounded pa-2"
      style="max-height: 200px; overflow-y: auto"
    >
      <v-btn
        v-for="icon in icons"
        :key="icon"
        :variant="modelValue === icon ? 'flat' : 'text'"
        :color="modelValue === icon ? 'primary' : 'default'"
        size="small"
        class="ma-1"
        style="min-width: 40px; height: 40px"
        @click="onInput(icon)"
      >
        <v-icon size="20">{{ icon }}</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  modelValue: String,
  label: { type: String, default: "Icon (mdi-*)" },
  placeholder: { type: String, default: "mdi-" },
  previewColor: { type: String, default: "primary" },
  density: { type: String, default: "compact" },
  // Caption above the grid of common icons
  gridLabel: { type: String, default: "Select from common icons:" },
  icons: {
    type: Array,
    default: () => [
      // Synced with ReportCategoriesSettings iconOptions
      "mdi-eye-plus",
      "mdi-comment-plus",
      "mdi-face-man",
      "mdi-shoe-print",
      "mdi-airplane-takeoff",
      "mdi-flight-takeoff",
      "mdi-account-group",
      "mdi-folder",
      "mdi-folder-open",
      "mdi-folder-multiple",
      "mdi-file-document",
      "mdi-file-document-outline",
      "mdi-file-multiple",
      "mdi-clipboard",
      "mdi-clipboard-text",
      "mdi-clipboard-check",
      "mdi-chart-line",
      "mdi-chart-bar",
      "mdi-chart-pie",
      "mdi-chart-timeline",
      "mdi-analytics",
      "mdi-trending-up",
      "mdi-trending-down",
      "mdi-poll",
      "mdi-alert",
      "mdi-alert-circle",
      "mdi-alert-triangle",
      "mdi-information",
      "mdi-information-outline",
      "mdi-check",
      "mdi-check-circle",
      "mdi-close-circle",
      "mdi-help-circle",
      "mdi-progress-check",
      "mdi-account",
      "mdi-account-group",
      "mdi-account-multiple",
      "mdi-account-circle",
      "mdi-face-man",
      "mdi-face-woman",
      "mdi-human",
      "mdi-human-male",
      "mdi-human-female",
      "mdi-family-tree",
      "mdi-medical-bag",
      "mdi-heart",
      "mdi-heart-pulse",
      "mdi-hospital",
      "mdi-pill",
      "mdi-thermometer",
      "mdi-stethoscope",
      "mdi-bandage",
      "mdi-wheelchair-accessibility",
      "mdi-home",
      "mdi-map-marker",
      "mdi-map-marker-circle",
      "mdi-crosshairs-gps",
      "mdi-airplane",
      "mdi-airplane-takeoff",
      "mdi-airplane-landing",
      "mdi-car",
      "mdi-train",
      "mdi-bus",
      "mdi-walk",
      "mdi-run",
      "mdi-bike",
      "mdi-ferry",
      "mdi-phone",
      "mdi-email",
      "mdi-message",
      "mdi-chat",
      "mdi-forum",
      "mdi-comment",
      "mdi-comment-multiple",
      "mdi-microphone",
      "mdi-speaker",
      "mdi-calendar",
      "mdi-calendar-today",
      "mdi-clock",
      "mdi-clock-outline",
      "mdi-timer",
      "mdi-alarm",
      "mdi-history",
      "mdi-schedule",
      "mdi-star",
      "mdi-star-outline",
      "mdi-flag",
      "mdi-flag-outline",
      "mdi-priority-high",
      "mdi-priority-low",
      "mdi-bookmark",
      "mdi-bookmark-outline",
      "mdi-shield",
      "mdi-shield-check",
      "mdi-security",
      "mdi-key",
      "mdi-lock",
      "mdi-lock-open",
      "mdi-eye",
      "mdi-eye-off",
      "mdi-camera",
      "mdi-video",
      "mdi-briefcase",
      "mdi-office-building",
      "mdi-school",
      "mdi-bank",
      "mdi-gavel",
      "mdi-scale-balance",
      "mdi-handshake",
      "mdi-domain",
      "mdi-tools",
      "mdi-wrench",
      "mdi-cog",
      "mdi-settings",
      "mdi-pencil",
      "mdi-pen",
      "mdi-brush",
      "mdi-printer",
      "mdi-calculator",
      "mdi-food",
      "mdi-silverware",
      "mdi-coffee",
      "mdi-bottle-wine",
      "mdi-water",
      "mdi-shower",
      "mdi-bed",
      "mdi-sofa",
      "mdi-currency-eur",
      "mdi-cash",
      "mdi-credit-card",
      "mdi-wallet",
      "mdi-piggy-bank",
      "mdi-receipt",
      "mdi-finance",
      "mdi-book",
      "mdi-book-open",
      "mdi-library",
      "mdi-note",
      "mdi-notebook",
      "mdi-pencil-ruler",
      "mdi-school-outline",
      "mdi-graduation-cap",
      "mdi-tag",
      "mdi-package",
      "mdi-gift",
      "mdi-shopping",
      "mdi-cart",
      "mdi-basket",
      "mdi-umbrella",
      "mdi-weather-sunny",
      "mdi-weather-rainy",
      "mdi-lightbulb",
      "mdi-flash",
      "mdi-fire",
      "mdi-water-drop",
      "mdi-leaf",
      "mdi-tree",
    ],
  },
});

const emit = defineEmits(["update:modelValue"]);

function onInput(value) {
  emit("update:modelValue", value);
}

const isValidIcon = computed(
  () => props.modelValue && props.modelValue.startsWith("mdi-"),
);
</script>

<style scoped>
.icon-grid .v-btn {
  min-width: 40px;
}
</style>
